import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { finalize, first } from 'rxjs/operators';
import { IDataRequest, IDuaDetails, IDuaDocuments, IResearchTeam } from '@vivli/features/data-requests/infrastructure/interface';
import { useOrganizations } from '@vivli/features/organizations/infrastructure/hook';
import {
    useActiveUserContext,
    useAssignedAppTypeContext,
    useDataRequestsApiContext,
    useModalApiContext,
    useMyDataRequestsApiContext,
    useToastApiContext,
    useVirtualMachineApiContext,
    useVivliConfigContext
} from '@vivli/core/infrastructure/context';
import { useTranslation } from 'react-i18next';
import { IVirtualMachine } from '@vivli/features/virtual-machine/infrastructure/interface';
import { DataRequestContext } from '@vivli/features/data-requests/infrastructure/context';
import { DataRequestStatusEnum, RequestedStudyTypeEnum } from '@vivli/features/data-requests/infrastructure/enum';
import { useFormContext, UseFormReturn, useFormState, useWatch } from 'react-hook-form';
import { PermissionsEnum, TranslationKeyEnum } from '@vivli/shared/infrastructure/enum';
import { useNavigate, useParams } from 'react-router';
import { useInitializeResearchTeamHook } from '@vivli/features/data-requests/infrastructure/hook';
import { findDuplicates } from '@vivli/shared/infrastructure/hook';
import { IStatusUpdate } from '@vivli/shared/infrastructure/interface';
import { selectSurveyTemplate, surveyHasUnansweredQuestions } from '@vivli/features/data-requests/infrastructure/service';

interface DataRequestContextWrapperProps {
    children: ReactNode;
    dataRequest: IDataRequest;
}

export const DataRequestContextWrapper = ({ children, dataRequest }: DataRequestContextWrapperProps) => {
    const { organizations } = useOrganizations();
    const { status } = useParams();
    const navigate = useNavigate();
    const { user, hasPermission, orgHasPermission } = useActiveUserContext();
    const { t } = useTranslation();
    const { transformDataRequest } = useInitializeResearchTeamHook();
    const { updateMyDataRequest } = useMyDataRequestsApiContext();
    const formApi = useFormContext<IDataRequest>();
    const { isDirty } = useFormState();

    const [dataRequestState, setDataRequest] = useState<IDataRequest>(dataRequest);
    const [showApprovalButtons, setShowApprovalButtons] = useState<boolean>(false);
    const [virtualMachine, setVirtualMachine] = useState<IVirtualMachine>();
    const [showResearchResults, setShowResearchResults] = useState(false);
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [isSavingDataRequest, setIsSavingDataRequest] = useState(false);
    const [isSubmittingDataRequest, setIsSubmittingDataRequest] = useState(false);
    const [statusUpdateSignal, setStatusUpdateSignal] = useState(false);
    const [isAdminEditing, setIsAdminEditing] = useState(false);
    const [planningTableData, setPlanningTableData] = useState([]);
    const [duaDetails, setDuaDetails] = useState<IDuaDetails>();
    const [duaDocuments, setDuaDocuments] = useState<IDuaDocuments[]>([]);
    const [statusUpdateState, setStatusUpdateState] = useState<IStatusUpdate>();
    const [includeRiskScore, setIncludeRiskScore] = useState<boolean>(false);
    const [surveyHasErrors, setSurveyHasErrors] = useState<boolean>(false);

    const { isAmr } = useAssignedAppTypeContext();
    const vivliConfig = useVivliConfigContext();
    const isVivliAdmin = hasPermission(PermissionsEnum.DrActAdv);

    // DEV-641: the AI/ML survey used to validate itself only while the Research Proposal tab was
    // mounted, so a request whose survey was never answered left surveyHasErrors false and the
    // Submit button enabled from any other tab. Watch the form values (not the loaded request) so
    // answering the survey re-enables Submit without needing a save first.
    // DEV-1040: useWatch, not formApi.watch. The React Compiler caches a watch() result until the
    // form context object changes, which only happens on a formState change - so the values loaded
    // by reset() could be missed and the empty-form verdict left Submit disabled. useWatch
    // subscribes to the two fields, so a reset or an answer always re-runs the check.
    const [surveyVersion, surveyAnswers] = useWatch({
        control: formApi.control,
        name: ['researchProposal.surveyVersion', 'researchProposal.surveyAnswers']
    });

    const surveyTemplate = useMemo(() => selectSurveyTemplate(vivliConfig?.surveyTemplates, surveyVersion), [vivliConfig, surveyVersion]);

    useEffect(() => {
        //AMR has no Research Proposal tab and no survey
        if (isAmr) {
            return;
        }

        //This runs the same SurveyJS model the Research Proposal tab runs, so the two always reach
        //the same verdict and it is safe to clear the flag here as well as set it. A request that
        //has never had a version stamped is measured against the current template - the same thing
        //that happens the moment the user does open that tab.
        setSurveyHasErrors(surveyHasUnansweredQuestions(surveyTemplate?.surveyTemplate, surveyAnswers));
    }, [isAmr, surveyTemplate, surveyAnswers]);

    const ownsDataRequest = () => {
        return dataRequest.userId === user.userId;
    };

    const dataRequestsService = useDataRequestsApiContext();
    const virtualMachineService = useVirtualMachineApiContext();
    const modalService = useModalApiContext();
    const toastService = useToastApiContext();
    const isSavingRef = useRef(false);

    const updateShowResearchResults = () => {
        const showResearchResultsState =
            (status === 'InProgress' ||
                dataRequestState?.status === DataRequestStatusEnum.PartiallyFulfilled ||
                dataRequestState?.status === DataRequestStatusEnum.Fulfilled ||
                status === 'Fulfilled' ||
                status === 'Active') &&
            !!virtualMachine;

        setShowResearchResults(showResearchResultsState);
    };

    const isActiveUserOrgAdmin = (): boolean => {
        return (
            user.orgMemberships.find(
                (om) =>
                    orgHasPermission(om.orgId, PermissionsEnum.ProviderActAdv) &&
                    dataRequest.requestedStudies.some((a) => a.organizationId === om.orgId)
            ) !== undefined
        );
    };
    const updateShowApprovalButtons = () => {
        if (!dataRequestState) {
            return;
        }

        //TODO PERMFE - redo with permissions+
        const isIrpApprover =
            user.orgMemberships.find(
                (om) =>
                    orgHasPermission(om.orgId, PermissionsEnum.IRPApprove) &&
                    orgHasPermission(om.orgId, PermissionsEnum.ProviderActAdv) &&
                    dataRequest.requestedStudies.some((a) => a.approvalBodyOrgId == om.orgId)
            ) !== undefined;
        const isDataProvider =
            user.orgMemberships.find(
                (om) =>
                    orgHasPermission(om.orgId, PermissionsEnum.DataContrib) &&
                    orgHasPermission(om.orgId, PermissionsEnum.ProviderActAdv) &&
                    dataRequest.requestedStudies.some((a) => a.organizationId == om.orgId)
            ) !== undefined;

        switch (dataRequestState.status) {
            case DataRequestStatusEnum.AwaitingIRPApproval:
            case DataRequestStatusEnum.RejectedByIRP:
            case DataRequestStatusEnum.RevisionsRequestedByIRP:
                setShowApprovalButtons(isIrpApprover);
                break;
            case DataRequestStatusEnum.AwaitingDataProviderApproval:
            case DataRequestStatusEnum.RejectedByDataProvider:
            case DataRequestStatusEnum.RevisionsRequestedByDataProvider:
                setShowApprovalButtons(isDataProvider);
                break;
            case DataRequestStatusEnum.SubmittedToVivli:
            case DataRequestStatusEnum.RejectedByVivli:
            case DataRequestStatusEnum.AwaitingDUAValidation:
            case DataRequestStatusEnum.DUAReturnedToRequester:
            case DataRequestStatusEnum.Approved:
                setShowApprovalButtons(isVivliAdmin);
                break;
            default:
                setShowApprovalButtons(false);
                break;
        }
    };

    //Methods for data-request form components

    const updateIsSaving = (status: boolean) => {
        isSavingRef.current = status;
        setIsSavingDataRequest(status);
    };

    const handleError = (error: string) => {
        const cleanError = error.replace(/\\r|\\n|"/g, ' ').trim();
        modalService.error(cleanError);
        updateIsSaving(false);
        setIsSubmittingDataRequest(false);
    };

    const handleDataRequestFormUpdate = (updatedDataRequest: IDataRequest) => {
        updateIsSaving(false);
        formApi.reset(updatedDataRequest, { keepErrors: true, keepIsValid: true });
        setDataRequest(updatedDataRequest);
    };

    const handleDataPackageCreation = () => {
        if (!dataRequest.metaDataDataPackageId) {
            dataRequestsService
                .createDataPackageForRequest(dataRequest.id)
                .pipe(first())
                .subscribe((updatedDataRequest) => {
                    handleDataRequestFormUpdate({
                        ...updatedDataRequest,
                        researchTeam: dataRequest.researchTeam, // data package creation nulls out research team
                        researchProposal: dataRequest.researchProposal // data package creation nulls out research team
                    });
                });
        }
    };

    const submitDataRequest = (updatedDataRequest: IDataRequest) => {
        const routeToNavigate = isAmr
            ? '/mydatarequests/active' //AMR uses My Data Requests
            : '/admin/requests/active'; //CT does not
        setIsSubmittingDataRequest(true);
        dataRequestsService
            .submitDataRequest(updatedDataRequest)
            .pipe(first())
            .subscribe((resultDataRequest) => {
                if (isAmr) {
                    updateMyDataRequest(resultDataRequest);
                }
                navigate(routeToNavigate);
            }, handleError);
    };

    const handleConfirmStep = (updatedDataRequest: IDataRequest) => {
        if (updatedDataRequest.requestedStudies.some((a) => a.requestedStudyType === RequestedStudyTypeEnum.External)) {
            modalService.confirm(t(TranslationKeyEnum.pathway2SubmitConfirmText), {
                title: 'Confirm Data Request is Ready to Submit',
                onConfirm: () => {
                    submitDataRequest(updatedDataRequest);
                },
                onCancel: () => {
                    handleDataRequestFormUpdate(updatedDataRequest);
                }
            });
        } else {
            submitDataRequest(updatedDataRequest);
        }
    };

    const handleDataRequestSaved = (updatedDataRequest: IDataRequest, submitRequest: boolean) => {
        toastService.success(`${updatedDataRequest.requestTitle} successfully saved`);

        if (submitRequest) {
            handleConfirmStep(updatedDataRequest);
        } else {
            handleDataRequestFormUpdate(updatedDataRequest);
        }
    };

    const handleFormSave = (submitRequest = false, forceSave = false) => {
        // already in the process of a form save, don't do any additional saves (yet)
        // also ignore if no fields have changed
        if (isSavingRef.current) {
            return;
        }

        formApi.handleSubmit(
            // handles form without validation errors (save and/or submit)
            (formValues) => handleFormApiSave(formValues, submitRequest, forceSave),
            () => {
                // handles form with validation errors (save only, no submit)
                const formValues = formApi.getValues();
                handleFormApiSave(formValues, submitRequest, forceSave);
            }
        )();
    };

    function checkResearchTeamForDuplicates(researchTeam: IResearchTeam) {
        const teamMembers = researchTeam.researchTeamDetails;
        // DEV-458: null-safe — a member without an email must not NRE the duplicate pre-check.
        const teamMemberEmails = teamMembers.filter((tm) => tm.email).map((tm) => tm.email.toLowerCase());
        const dupEmails = findDuplicates(teamMemberEmails);
        const teamMemberOrcidIds = teamMembers.filter((tm) => tm.orcidId).map((tm) => tm.orcidId.toLowerCase());
        const dupOrcidIds = findDuplicates(teamMemberOrcidIds);
        if (dupEmails.length > 0 || dupOrcidIds.length > 0) {
            let message =
                'Each user email address or ORCID iD can appear in the research team only once - please delete any duplicate users.  If a user will serve as both the Lead Researcher and Statistician, use the "Lead Researcher is also Statistician Researcher" checkbox to indicate this.' +
                `The following team members have duplicate emails or ORCID iDs: ${dupEmails.join(',')} ${dupOrcidIds.join(',')}`;
            modalService.error(message);
            return false;
        }
        return true;
    }

    const handleFormApiSave = (formValues: IDataRequest, submitRequest: boolean, forceSave = false) => {
        let updatedDataRequest: IDataRequest = { ...formValues };

        // DEV-691: isDirty is a per-render snapshot. A caller that changes a value and saves in
        // the same tick (the title prompt) is still holding the pre-edit value here, which
        // silently dropped title-only edits - such a caller passes forceSave instead.
        if (forceSave || isDirty) {
            // only do another request update if needed (aka data has changed)
            updateIsSaving(true);
            //check Research team for duplicates
            if (submitRequest && !isAmr && !checkResearchTeamForDuplicates(updatedDataRequest.researchTeam)) {
                //save, but not submit
                handleFormApiSave(formValues, false, forceSave);
                return;
            }

            dataRequestsService
                .updateDataRequest(updatedDataRequest)
                .pipe(first())
                .subscribe((resultDataRequest) => {
                    handleDataRequestSaved(resultDataRequest, submitRequest);
                }, handleError);
        } else if (submitRequest) {
            handleConfirmStep(updatedDataRequest);
        }
    };

    const handleFormSavePublish = (submitRequest = false): Promise<IDataRequest> => {
        if (isSavingRef.current) {
            return Promise.resolve(null);
        }

        const formValues = formApi.getValues();
        let updatedDataRequest: IDataRequest = { ...formValues };

        // only do another request update if needed (aka data has changed)
        updateIsSaving(true);
        //check Research team for duplicates
        if (submitRequest && !isAmr && !checkResearchTeamForDuplicates(updatedDataRequest.researchTeam)) {
            //save, but not submit
            handleFormApiSave(formValues, false);
            return Promise.resolve(null);
        }

        return new Promise<IDataRequest>((resolve, reject) => {
            dataRequestsService
                .updateDataRequest(updatedDataRequest)
                .pipe(first())
                .subscribe(
                    (resultDataRequest) => {
                        handleDataRequestSaved(resultDataRequest, submitRequest);
                        resolve(resultDataRequest);
                    },
                    (error) => {
                        handleError(error);
                        reject(error);
                    }
                );
        });
    };

    const handelDuaSave = () => {
        setIsSavingDataRequest(true);
        const updatedDataRequest = {
            ...dataRequestState,
            duaDetail: {
                ...duaDetails,
                contributorTemplates: planningTableData,
                duaDocuments: duaDocuments
            },
            statusUpdate: {
                ...statusUpdateState
            }
        };
        dataRequestsService.updateDataRequest(updatedDataRequest).subscribe(
            (resultDataRequest) => {
                toastService.success(`DUA Details successfully saved`);
                setDataRequest(resultDataRequest);
                setIsSavingDataRequest(false);
            },
            (err) => {
                modalService.error(`An error occurred with your operation. Please try again or contact Vivli support.`);
                setIsSavingDataRequest(false);
            }
        );
    };

    function handleSavePdfVersion() {
        setIsSavingDataRequest(true);
        dataRequestsService
            .saveDataRequestPdf(dataRequestState.id)
            .pipe(
                first(),
                finalize(() => setIsSavingDataRequest(false))
            )
            .subscribe((resultDataRequest) => {
                toastService.success(`PDF version successfully saved`);
                setDataRequest(resultDataRequest);
            }, handleError);
    }

    ///Repurposed for use with secondary
    ///doi creation or update, does this doi action
    /// and saves the request
    const handlePublishingSaveWithSecondaryDoi = () => {
        setIsSavingDataRequest(true);
        const updatedDataRequest = {
            ...dataRequestState
        };
        dataRequestsService.createOrUpdateDoi(updatedDataRequest).subscribe(
            (resultDataRequest) => {
                toastService.success(`Doi successfully saved`);
                setDataRequest(resultDataRequest);
                setIsSavingDataRequest(false);
                //necessary in case of a create, not an update, but no problem either way.
                formApi.setValue(`secondaryAnalysisDoi`, resultDataRequest.secondaryAnalysisDoi);
            },
            (err) => {
                modalService.error(`An error occurred with your operation. Please try again or contact Vivli support.`);
                setIsSavingDataRequest(false);
            }
        );
    };

    const handleEditTitleClick = () => {
        modalService.prompt('Enter a new title for your research data request', formApi.getValues('requestTitle'), {
            title: 'Research Data Request Title',
            promptLabel: 'Title',
            cancelText: 'Cancel',
            confirmText: 'Save',
            onConfirm: (newTitle) => {
                const currentTitle = formApi.getValues('requestTitle');
                const title = newTitle?.comment ? newTitle.comment : currentTitle;
                if (title === currentTitle) {
                    //nothing was actually changed, so there is nothing to save
                    return;
                }

                formApi.setValue('requestTitle', title, { shouldDirty: true });
                //DEV-691: forceSave - the dirty flag the save gate reads is a render-scope
                //snapshot, so it hasn't caught up with the setValue above yet.
                handleFormSave(false, true);
            }
        });
    };

    const getResetToDraftVisibility = () => {
        switch (dataRequestState.status) {
            case DataRequestStatusEnum.SubmittedToVivli:
            case DataRequestStatusEnum.RejectedByVivli:
            case DataRequestStatusEnum.AwaitingDataProviderApproval:
            case DataRequestStatusEnum.AwaitingIRPApproval:
            case DataRequestStatusEnum.RevisionsRequestedByDataProvider:
            case DataRequestStatusEnum.RejectedByDataProvider:
            case DataRequestStatusEnum.RevisionsRequestedByIRP:
            case DataRequestStatusEnum.RejectedByIRP:
            case DataRequestStatusEnum.Approved:
            case DataRequestStatusEnum.AwaitingDUAValidation:
            case DataRequestStatusEnum.DUAValidated:
            case DataRequestStatusEnum.DUAReturnedToRequester:
                if (isVivliAdmin) {
                    //TODO PERMFE - redo with permissions+
                    return true;
                }
                break;
            case DataRequestStatusEnum.Cancelled:
                return true;
            case DataRequestStatusEnum.Fulfilled:
            case DataRequestStatusEnum.PartiallyFulfilled:
                if (!virtualMachine && isVivliAdmin) {
                    //TODO PERMFE - redo with permissions+
                    return true;
                }
                break;
            case DataRequestStatusEnum.Draft:
            case DataRequestStatusEnum.Archived:
            default:
                return false;
        }
    };

    const dateEditingEnabled = useMemo(() => {
        return dataRequestState.status === DataRequestStatusEnum.Draft || isVivliAdmin;
    }, [dataRequestState.status]);
    //End section

    const provider = {
        dataRequest: dataRequestState,
        updateDataRequest: (dataRequest) => {
            const updatedDataRequest = transformDataRequest(dataRequest);
            updateMyDataRequest(updatedDataRequest);
            setDataRequest(updatedDataRequest);
            formApi.reset(updatedDataRequest, { keepErrors: true, keepIsValid: true });
        },
        showApprovalButtons,
        setShowApprovalButtons,
        organizations,
        virtualMachine,
        showResearchResults,
        isUpdatingStatus,
        setIsUpdatingStatus,
        handleEditTitleClick,
        isSavingDataRequest,
        isSubmittingDataRequest,
        handleFormSave,
        handleFormSavePublish,
        handleDataPackageCreation,
        getResetToDraftVisibility,
        updateIsSaving,
        isAdminEditing,
        setIsAdminEditing,
        ownsDataRequest,
        statusUpdateSignal,
        setStatusUpdateSignal,
        handelDuaSave,
        handlePublishingSaveWithSecondaryDoi,
        planningTableData,
        setPlanningTableData,
        duaDetails,
        setDuaDetails,
        statusUpdateState,
        setStatusUpdateState,
        duaDocuments,
        setDuaDocuments,
        includeRiskScore,
        setIncludeRiskScore,
        surveyHasErrors,
        setSurveyHasErrors,
        handleSavePdfVersion,
        dateEditingEnabled,
        isActiveUserOrgAdmin
    };

    useEffect(() => {
        if (!dataRequestState) {
            return;
        }

        updateShowResearchResults();
        updateShowApprovalButtons();
    }, [dataRequestState]);

    useEffect(() => {
        if (!virtualMachine && dataRequest) {
            virtualMachineService.getDataRequestVm(dataRequest.id).pipe(first()).subscribe(setVirtualMachine, modalService.error);
        }
    }, [dataRequest]);

    useEffect(() => {
        updateShowResearchResults();
    }, [virtualMachine]);

    useEffect(() => {
        if (dataRequestState?.duaDetail?.contributorTemplates) {
            setPlanningTableData(dataRequestState.duaDetail.contributorTemplates);
            setDuaDetails(dataRequestState.duaDetail);
        }
        dataRequestState.duaDetail?.duaDocuments && setDuaDocuments(dataRequestState.duaDetail.duaDocuments);
        dataRequestState.statusUpdate && setStatusUpdateState(dataRequestState.statusUpdate);
    }, [dataRequestState]);

    return <DataRequestContext.Provider value={provider}>{children}</DataRequestContext.Provider>;
};
